import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, AlertTriangle, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelAppointmentPage() {
  const { token } = useParams();
  const [apt, setApt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [result, setResult] = useState(null); // "success" | "already" | "error"
  const [error, setError] = useState("");
  const [configData, setConfigData] = useState(null);

  const phone = configData?.phone || "916003800";

  useEffect(() => {
    axios.get(`${API.replace('/api','')}/api/config`).then(r => setConfigData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/appointments/action/${token}`)
      .then(res => { setApt(res.data.appointment); })
      .catch(() => { setError("Enlace invalido o expirado."); })
      .finally(() => setLoading(false));
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/appointments/cancel/${token}`);
      if (res.data.status?.includes("cancelada")) {
        setResult("already");
      } else {
        setResult("success");
      }
    } catch {
      setResult("error");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="cancel-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-2">Enlace no valido</h2>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (result === "success") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="cancel-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="heading-industrial text-2xl font-bold text-slate-900 uppercase mb-3">Cita Cancelada</h2>
          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            Tu cita ha sido cancelada correctamente. El taller ha sido notificado.
          </p>
          <p className="text-sm text-slate-500 mb-8">Si necesitas reservar una nueva cita, puedes hacerlo desde nuestra web.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-5" data-testid="cancel-go-home">Inicio</Button>
            </Link>
            <Link to="/cita">
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-5" data-testid="cancel-new-apt">Nueva Cita</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (result === "already") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="cancel-already">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-2">Cita ya cancelada</h2>
          <p className="text-sm text-slate-600 mb-6">Esta cita ya habia sido cancelada previamente.</p>
          <Link to="/">
            <Button variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold">Volver al inicio</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (result === "error") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="cancel-failed">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-2">Error</h2>
          <p className="text-sm text-slate-600 mb-6">No se pudo cancelar la cita. Intentalo de nuevo o llama al {phone}.</p>
          <Button onClick={() => setResult(null)} variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold">Reintentar</Button>
        </div>
      </div>
    );
  }

  // Confirmation screen before cancelling
  const alreadyCancelled = apt?.status?.includes("cancelada");

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-lg mx-auto px-4" data-testid="cancel-confirm-page">
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-widest text-red-600 font-semibold">Cancelar cita</span>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-2 uppercase">Cancelar tu cita</h1>
        </div>

        <Card className="border-slate-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            {alreadyCancelled ? (
              <div className="text-center py-4">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <p className="text-sm text-slate-600">Esta cita ya esta cancelada.</p>
              </div>
            ) : (
              <>
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-6">
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Datos de la cita</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <span className="text-slate-500">Cliente</span>
                    <span className="font-medium text-slate-900" data-testid="cancel-client-name">{apt?.client_name}</span>
                    <span className="text-slate-500">Matricula</span>
                    <span className="font-mono font-semibold text-slate-900">{apt?.plate}</span>
                    <span className="text-slate-500">Vehiculo</span>
                    <span className="font-medium text-slate-900">{apt?.brand} {apt?.model_name}</span>
                    <span className="text-slate-500">Centro</span>
                    <span className="font-medium text-slate-900">{apt?.center_name}</span>
                    <span className="text-slate-500">Servicio</span>
                    <span className="font-medium text-slate-900">{apt?.service_type}</span>
                    <span className="text-slate-500">Fecha</span>
                    <span className="font-medium text-slate-900">{apt?.date}</span>
                    <span className="text-slate-500">Hora</span>
                    <span className="font-mono font-medium text-slate-900">{apt?.time_slot}</span>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6">
                  <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Esta accion cancelara tu cita de forma definitiva.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Link to="/" className="flex-1">
                    <Button variant="outline" className="w-full rounded-sm text-xs uppercase tracking-wider font-semibold h-10" data-testid="cancel-keep-btn">
                      Mantener cita
                    </Button>
                  </Link>
                  <Button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider font-bold h-10"
                    data-testid="cancel-confirm-btn"
                  >
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar cancelación"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Si tienes dudas, llamanos al <strong>{phone}</strong>
        </p>
      </div>
    </div>
  );
}
